import { useSceneEvents } from "@/components/common/scene-events-context";
import {
  SubScene,
  useOnResize,
  useTypedEvent,
} from "@faro-lotv/app-component-toolbox";
import { BasicRenderingPolicy, SceneFilterFunction } from "@faro-lotv/lotv";
import { useCallback, useEffect, useMemo, type JSX } from "react";

type SplatsSubSceneProps = {
  /** Function to select from the scene graph only the elements to be rendered in this subscene. */
  filter: SceneFilterFunction;

  /** Whether the subscene is enabled */
  enabled?: boolean;

  /** Whether this subscene should be rendered on demand */
  renderOnDemand?: boolean;
};

/**
 * Rasterizing a few million splats is expensive enough to dominate the frame time, so the subscene
 * framebuffer is kept and only recomputed when the camera moves or the splats themselves change.
 *
 * @param renderOnDemand Whether the subscene may skip re-rendering while nothing changes
 * @returns the rendering policy to assign to the subscene rendering the splats
 */
function useSplatsRenderingPolicy(
  renderOnDemand: boolean,
): BasicRenderingPolicy {
  // There is no single object to watch: the splats signal their changes through the scene events.
  const renderingPolicy = useMemo(
    () => new BasicRenderingPolicy(undefined),
    [],
  );

  useEffect(() => {
    if (
      renderOnDemand &&
      !renderingPolicy.subScene?.cameraMonitor.cameraMoving
    ) {
      renderingPolicy.onCameraStoppedMoving();
    } else {
      renderingPolicy.onCameraStartedMoving();
    }
  }, [renderingPolicy, renderOnDemand]);

  const invalidate = useCallback(() => {
    renderingPolicy.subScene?.invalidate();
  }, [renderingPolicy]);

  const sceneEvents = useSceneEvents();
  useTypedEvent<void>(sceneEvents.invalidateGaussianSplatsScene, invalidate);
  useOnResize(invalidate);

  return renderingPolicy;
}

/** @returns a subscene rendering the splats on demand, to be composed by a ComposeFramebuffersPass */
export function SplatsSubScene({
  filter,
  enabled = true,
  renderOnDemand = true,
}: SplatsSubSceneProps): JSX.Element {
  const renderingPolicy = useSplatsRenderingPolicy(renderOnDemand);

  return (
    <SubScene
      filter={filter}
      enabled={enabled}
      renderingPolicy={renderingPolicy}
      transparentBackground
    />
  );
}
